import React, { useState, useEffect, useCallback } from 'react';
import { CarFront, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/api';
import axios from 'axios';
import { toast } from 'sonner';

const VO_CHECKLIST_ITEMS = [
  { key: 'nettoyage_exterieur', label: 'Nettoyage exterieur', options: ['Bon', 'Nettoyage a faire', 'Pas bon'] },
  { key: 'nettoyage_interieur', label: 'Nettoyage interieur', options: ['Bon', 'Nettoyage a faire', 'Pas bon'] },
  { key: 'etat_carrosserie', label: 'Etat de la carrosserie', options: ['Bon', 'Rayures legeres', 'Reparation necessaire', 'Pas bon'] },
  { key: 'etat_parechocs', label: 'Etat des pare-chocs', options: ['Bon', 'Rayures legeres', 'Reparation necessaire', 'Pas bon'] },
  { key: 'etat_jantes', label: 'Etat des jantes', options: ['Bon', 'Rayures legeres', 'Reparation necessaire', 'Pas bon'] },
  { key: 'etat_pneus', label: 'Etat des pneus', options: ['Bon', 'A remplacer', 'Pas bon'] },
  { key: 'etat_interieur', label: 'Etat de l\'interieur', options: ['Bon', 'Nettoyage a faire', 'Pas bon'] },
];

function getStatusStyle(val) {
  if (!val) return 'bg-slate-50 border-slate-200';
  if (val === 'Bon') return 'bg-emerald-50 border-emerald-300';
  if (val === 'Pas bon') return 'bg-red-50 border-red-300';
  return 'bg-amber-50 border-amber-300';
}

function getStatusDot(val) {
  if (!val) return 'bg-slate-300';
  if (val === 'Bon') return 'bg-emerald-500';
  if (val === 'Pas bon') return 'bg-red-500';
  return 'bg-amber-500';
}

export const PreparationOccasionSection = ({ woId, workOrder, canEdit = true }) => {
  const [expanded, setExpanded] = useState(true);
  const [checklist, setChecklist] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const loadChecklist = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work-orders/${woId}/preparation-checklist`);
      setChecklist(res.data.checklist || {});
      setNotes(res.data.notes || {});
    } catch { /* no checklist yet */ }
  }, [woId]);

  useEffect(() => { loadChecklist(); }, [loadChecklist]);

  const updateValue = async (key, value) => {
    if (!canEdit) return;
    const updated = { ...checklist, [key]: value };
    setChecklist(updated);
    try {
      await axios.put(`${API}/work-orders/${woId}/preparation-checklist`, { checklist: updated, notes });
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const updateNote = (key, noteText) => {
    setNotes(prev => ({ ...prev, [key]: noteText }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/work-orders/${woId}/preparation-checklist`, { checklist, notes });
      toast.success('Fiche sauvegardee');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const filledCount = VO_CHECKLIST_ITEMS.filter(item => checklist[item.key]).length;
  const totalCount = VO_CHECKLIST_ITEMS.length;

  const garageName = workOrder?.client_name || 'Votre garage';
  const vehicleInfo = [workOrder?.vehicle_brand, workOrder?.vehicle_model].filter(Boolean).join(' ') || 'Vehicule';
  const plateNumber = workOrder?.vehicle_plate || '';

  return (
    <Card className="rounded-xl overflow-hidden border-t-4 border-t-blue-600" data-testid="preparation-occasion-section">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between p-4 hover:bg-blue-50/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid="toggle-preparation"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <CarFront className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <span className="font-bold text-slate-900 text-sm block">Fiche Occasion</span>
            <span className="text-xs text-slate-500">{filledCount}/{totalCount} controles effectues</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Vehicle & client info bar */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-2 gap-3" data-testid="vo-vehicle-info">
            <div>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Client</span>
              <p className="text-sm font-semibold text-slate-800">{garageName}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Vehicule</span>
              <p className="text-sm font-semibold text-slate-800">{vehicleInfo}</p>
              {plateNumber && <p className="text-xs text-slate-500">{plateNumber}</p>}
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${filledCount === totalCount ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0}%` }} />
          </div>

          {/* Checklist items */}
          <div className="space-y-2" data-testid="vo-checklist-items">
            {VO_CHECKLIST_ITEMS.map(item => {
              const value = checklist[item.key] || '';
              const note = notes[item.key] || '';
              return (
                <div key={item.key} className={`rounded-lg border p-3 transition-colors ${getStatusStyle(value)}`}
                  data-testid={`vo-item-${item.key}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusDot(value)}`} />
                    <span className="text-sm font-medium text-slate-800 flex-1">{item.label}</span>
                    {canEdit ? (
                      <Select value={value || undefined} onValueChange={(v) => updateValue(item.key, v)}>
                        <SelectTrigger className="w-[180px] h-8 text-xs bg-white" data-testid={`vo-select-${item.key}`}>
                          <SelectValue placeholder="Selectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {item.options.map(opt => (
                            <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-sm font-semibold ${value === 'Bon' ? 'text-emerald-700' : value === 'Pas bon' ? 'text-red-700' : 'text-amber-700'}`}>
                        {value || '-'}
                      </span>
                    )}
                  </div>
                  {/* Note input */}
                  {canEdit && (
                    <Input
                      value={note}
                      onChange={e => updateNote(item.key, e.target.value)}
                      onBlur={saveAll}
                      placeholder="Remarque..."
                      className="mt-2 h-7 text-xs bg-white/70 border-slate-200"
                      data-testid={`vo-note-${item.key}`}
                    />
                  )}
                  {!canEdit && note && (
                    <p className="mt-1 text-xs text-slate-600 italic">{note}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Save button */}
          {canEdit && (
            <Button onClick={saveAll} disabled={saving} variant="outline" className="w-full text-sm" data-testid="vo-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder la fiche'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
